/**
 * GlobalMenuModal — gamified menu drawer with slide-from-right animation.
 * State and callbacks live here; rendering is split into MenuProfileSection + MenuItemsList.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { usePathname, useRouter, type Href } from 'expo-router';
import { useAnalytics } from '../../src/analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../src/analytics/events';
import { ANALYTICS_MENU_ITEMS } from '../../src/analytics/analytics.constants';
import {
  View,
  Text,
  Animated,
  ScrollView,
  useWindowDimensions,
  Platform,
  Linking,
} from 'react-native';
import { LogOut } from 'lucide-react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSupabaseAuth } from '../../src/shared/contexts/SupabaseAuthContext';
import { useMenuContext } from '../../src/layout/contexts/MenuContext';
import { useTheme } from '../../src/theme/useTheme';
import { useAvatarContext } from '../../src/avatars/contexts/AvatarContext';
import { useSnackbar } from '../../src/shared/hooks/useSnackbar';
import { useApiClient } from '../../helpers/apiClient.helper';
import {
  getImpersonationSession,
  subscribeImpersonationSession,
} from '../../src/zportiAdmin/helpers/impersonationToken.helper';
import { UNIMPERSONATE_PATH } from '../../src/zportiAdmin/constants/impersonationRoute.constants';
import { useEconomyStore } from '../../src/shared/stores/economyStore';
import { getCountdownDisplay } from '../../src/challenges/helpers/countdownToDate.helper';
import { DailyRewardModal } from '../../src/economy/components/DailyRewardModal';
import { CouponRedeemModal } from '../../src/economy/components/CouponRedeemModal/CouponRedeemModal';
import { PrivacyLegalDocumentModal } from './PrivacyLegalDocumentModal/PrivacyLegalDocumentModal';
import { PrivacyAccountDeletionModal } from './PrivacyAccountDeletionModal/PrivacyAccountDeletionModal';
import { PrivacyDataControlsModal } from './PrivacyDataControlsModal/PrivacyDataControlsModal';
import { SupportModal } from '../support/SupportModal/SupportModal';
import { OrgSwitcherFooterButton } from '../../src/accounts/components/OrgSwitcher/OrgSwitcherFooterButton';
import { runAfterNativeModalDismiss } from '../../src/shared/helpers/nativeModalDismiss.helper';
import { IDLE_COIN_SOURCE } from '../../constants/assets';
import { useQueryClient } from '@tanstack/react-query';
import { updateDisplayName, type ApiClientFn } from '../../src/shared/helpers/profileApi';
import { syncDisplayNameToSupabaseSession } from '../../src/shared/helpers/syncDisplayNameToSupabaseSession.helper';
import { splitFullNameToFirstAndLast } from '../../src/shared/helpers/displayNameSplit.helper';
import { sanitizeInput } from '../../src/helpers/sanitizeInput.helper';
import { containsProfanity } from '../../src/helpers/profanity.helper';
import { BaseModal } from '../ui/BaseModal';
import { MenuProfileSection } from './MenuProfileSection';
import { MenuItemsList } from './MenuItemsList';
import { SoundPressable } from '../../src/shared/components/SoundPressable/SoundPressable';
import {
  getGlobalMenuModalContentPosition,
  getGlobalMenuModalContentAlignment,
  getGlobalMenuModalStyles,
  MENU_DRAWER_WIDTH_FRACTION,
  MENU_DRAWER_MAX_WIDTH,
} from './GlobalMenuModal.styles';
import {
  DISPLAY_NAME_MIN_LENGTH,
  DISPLAY_NAME_MAX_LENGTH,
  DISPLAY_NAME_BEARING_QUERY_ROOTS,
  GLOBAL_MENU_COPY,
  GLOBAL_MENU_GUEST_UPGRADE,
  GLOBAL_MENU_MODAL_CONTENT_TEST_ID,
  GLOBAL_MENU_PRIVACY_SUBMENU_LEGAL_ANALYTICS,
} from './globalMenuModal.constants';
import { buildPublicSignInHref } from '../../src/signIn/helpers/postAuthRedirect.helper';
import { useDialog } from '../../src/shared/hooks/useDialog';
import { useSound } from '../../src/shared/hooks/useSound';
import { soundService } from '../../src/shared/services/SoundService';
import { SOUND_IDS } from '../../src/shared/constants/sounds.constants';
import { useGuestGuard } from '../../src/shared/hooks/useGuestGuard';
import { GUEST_AUTH_ACTIONS } from '../../src/shared/hooks/useGuestGuard.constants';
import { useWorldCupWelcomeMenuStore } from '../../src/worldcup/stores/worldCupWelcomeMenuStore';
import type { InAppLegalDocumentVariant } from '../../src/landing/types/legalDocument.types';
import { PRODUCTION_APP_ORIGIN } from '../../constants/env';
import { getDailyClaimSuccessSoundId } from '../../src/economy/helpers/dailyClaimGameSound.helper';

const SLIDE_DURATION_MS = 250;
const EDIT_MODE_DELAY_MS = 80;

export const GlobalMenuModal: React.FC<{
  testID?: string;
  disableDrawerAnimation?: boolean;
  disableAnimations?: boolean;
}> = ({ testID, disableDrawerAnimation = false, disableAnimations = false }) => {
  const skipDrawerMotion = disableDrawerAnimation || disableAnimations;
  const showWebPressableTransition = !disableAnimations;
  const { width: windowWidth } = useWindowDimensions();
  const drawerWidthPx = Math.min(windowWidth * MENU_DRAWER_WIDTH_FRACTION, MENU_DRAWER_MAX_WIDTH);
  const slideAnim = useRef(new Animated.Value(drawerWidthPx)).current;
  const [isClosing, setIsClosing] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => { return () => { isMountedRef.current = false; }; }, []);

  const { user, isAnonymous } = useSupabaseAuth();
  const openWorldCupRulesFromMenu = useWorldCupWelcomeMenuStore((s) => s.openWorldCupRulesFromMenu);
  const pathname = usePathname();
  const router = useRouter();
  const { menuVisible, setMenuVisible, handleSignOut, openTutorialFromMenu, openLayeredAvatarLabFromMenu } = useMenuContext();
  const { currentAvatarId, currentAvatarConfig } = useAvatarContext();
  const { showSnackbar } = useSnackbar();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const coins = useEconomyStore((s) => s.coins);
  const claimDaily = useEconomyStore((s) => s.claimDaily);
  const redeemCoupon = useEconomyStore((s) => s.redeemCoupon);
  const { playSound, isMuted, toggleMute } = useSound();
  const menuOpenSwishPlayedRef = useRef(false);
  const [isDailyRewardModalVisible, setIsDailyRewardModalVisible] = useState(false);
  const [isCouponRedeemModalVisible, setIsCouponRedeemModalVisible] = useState(false);
  // Expanded by default so "Delete your account" is immediately visible (App Store Guideline 5.1.1(v)).
  const [isPrivacySubmenuExpanded, setIsPrivacySubmenuExpanded] = useState(true);
  const [privacyLegalVariant, setPrivacyLegalVariant] = useState<InAppLegalDocumentVariant | null>(null);
  const [isPrivacyAccountDeletionModalVisible, setIsPrivacyAccountDeletionModalVisible] = useState(false);
  const [isPrivacyDataControlsModalVisible, setIsPrivacyDataControlsModalVisible] = useState(false);
  const [isSupportModalVisible, setIsSupportModalVisible] = useState(false);
  const { track } = useAnalytics();
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [isEditingDisplayName, setIsEditingDisplayName] = useState(false);
  const [draftFullName, setDraftFullName] = useState('');
  const [optimisticDisplayName, setOptimisticDisplayName] = useState<string | null>(null);
  const editModeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const styles = useMemo(() => getGlobalMenuModalStyles(theme, insets), [theme, insets]);
  const { showAlert } = useDialog();
  const { requireAuth } = useGuestGuard();

  const impersonationSession = useSyncExternalStore(
    subscribeImpersonationSession,
    getImpersonationSession,
    getImpersonationSession,
  );
  const isImpersonatingSession = Boolean(impersonationSession?.token);
  const impersonationTargetLabel = impersonationSession?.targetLabel ?? null;

  const isModalVisible = menuVisible || isClosing;

  useEffect(() => {
    if (pathname.includes('/sign-up') || pathname.includes('/sign-in')) {
      setMenuVisible(false); setIsClosing(false); setOptimisticDisplayName(null); slideAnim.setValue(drawerWidthPx);
    }
  }, [pathname, setMenuVisible, drawerWidthPx, slideAnim]);

  useEffect(() => {
    if (!menuVisible) { setIsClosing(false); slideAnim.setValue(drawerWidthPx); }
  }, [menuVisible, drawerWidthPx, slideAnim]);

  /** One swish per menu open (hamburger / programmatic); reset when fully closed. */
  useEffect(() => {
    if (menuVisible && !isClosing) {
      if (!menuOpenSwishPlayedRef.current) {
        menuOpenSwishPlayedRef.current = true;
        void soundService.warmFromUserGesture();
        playSound(SOUND_IDS.UI_MENU_OPEN_SWISH);
      }
    } else if (!menuVisible) {
      menuOpenSwishPlayedRef.current = false;
    }
  }, [menuVisible, isClosing, playSound]);

  useEffect(() => {
    if (menuVisible && !isClosing) {
      if (skipDrawerMotion) { slideAnim.setValue(0); return; }
      slideAnim.setValue(drawerWidthPx);
      Animated.timing(slideAnim, { toValue: 0, duration: SLIDE_DURATION_MS, useNativeDriver: Platform.OS !== 'web' }).start();
    }
  }, [menuVisible, isClosing, drawerWidthPx, slideAnim, skipDrawerMotion]);

  const handleDismiss = useCallback((onAfterClose?: () => void) => {
    // Guard: BaseModal's onRequestClose (Android back button) can forward a native
    // event object here, so only invoke onAfterClose when it's actually a function.
    const runAfterClose = () => { if (typeof onAfterClose === 'function') onAfterClose(); };
    if (isClosing) return;
    if (skipDrawerMotion) {
      setMenuVisible(false); setIsClosing(false); setOptimisticDisplayName(null); slideAnim.setValue(drawerWidthPx); runAfterClose(); return;
    }
    setIsClosing(true);
    Animated.timing(slideAnim, { toValue: drawerWidthPx, duration: SLIDE_DURATION_MS, useNativeDriver: Platform.OS !== 'web' }).start(({ finished }) => {
      if (finished) { setMenuVisible(false); setIsClosing(false); setOptimisticDisplayName(null); slideAnim.setValue(drawerWidthPx); runAfterClose(); }
    });
  }, [isClosing, skipDrawerMotion, setMenuVisible, slideAnim, drawerWidthPx]);

  /** Account switch from the footer: the menu's Modal must be fully gone first (iOS). */
  const closeMenuThenSwitch = useCallback(
    (then: () => void) => handleDismiss(() => runAfterNativeModalDismiss(then)),
    [handleDismiss],
  );

  const openOfficialWebsite = useCallback(() => {
    track(ANALYTICS_EVENTS.MENU_ITEM_CLICKED, { menu_item: ANALYTICS_MENU_ITEMS.OFFICIAL_WEBSITE });
    handleDismiss(() => {
      if (Platform.OS === 'web') { router.push('/' as Href); return; }
      const origin = PRODUCTION_APP_ORIGIN.replace(/\/$/, '');
      void Linking.openURL(`${origin}/`).catch(() => {
        void showAlert({ title: 'Could not open', message: 'Unable to open the website. Try again later.', type: 'danger' });
      });
    });
  }, [track, handleDismiss, router, showAlert]);

  const openDailyRewardModal = useCallback(() => {
    if (!requireAuth(GUEST_AUTH_ACTIONS.DAILY_REWARD)) return;
    setIsDailyRewardModalVisible(true);
  }, [requireAuth]);

  const openCouponRedeemModal = useCallback(() => {
    track(ANALYTICS_EVENTS.MENU_ITEM_CLICKED, { menu_item: ANALYTICS_MENU_ITEMS.REDEEM_COUPON });
    if (!requireAuth(GUEST_AUTH_ACTIONS.REDEEM_COUPON)) return;
    setIsCouponRedeemModalVisible(true);
  }, [requireAuth, track]);

  const handleRedeemCouponSubmit = useCallback(async (code: string) => {
    if (!requireAuth(GUEST_AUTH_ACTIONS.REDEEM_COUPON)) return { success: false, error: 'Sign in to redeem a coupon' };
    const result = await redeemCoupon(
      apiClient as (endpoint: string, options?: Record<string, unknown>) => Promise<{ status: number; data: unknown }>,
      code,
    );
    if (!result.success) {
      if (result.error === 'SIGN_IN_REQUIRED') { void requireAuth(GUEST_AUTH_ACTIONS.REDEEM_COUPON); return result; }
      return result;
    }
    if (typeof result.awarded === 'number') track(ANALYTICS_EVENTS.COUPON_REDEEMED, { awarded: result.awarded });
    // Same SFX id as daily claim: native uses daily-claim.mp3; web uses the paired synth preset (see SoundService.play).
    playSound(SOUND_IDS.DAILY_CLAIM);
    return result;
  }, [apiClient, redeemCoupon, requireAuth, track, playSound]);

  const handleClaimDaily = useCallback(async () => {
    if (!requireAuth(GUEST_AUTH_ACTIONS.DAILY_REWARD)) return;
    const preClaim = useEconomyStore.getState();
    const successSoundId = getDailyClaimSuccessSoundId(
      preClaim.dailyClaimStreak,
      preClaim.lastDailyClaimAt
    );
    const result = await claimDaily(apiClient as (endpoint: string, options?: Record<string, unknown>) => Promise<{ status: number; data: unknown }>);
    if (result.success) {
      playSound(successSoundId);
    }
    if (!result.success) {
      if (result.error === 'SIGN_IN_REQUIRED') { void requireAuth(GUEST_AUTH_ACTIONS.DAILY_REWARD); return; }
      if (result.retryAfterMs) {
        showSnackbar(`Available in ${getCountdownDisplay(new Date(Date.now() + result.retryAfterMs))}. Coins reset at UTC midnight.`, 'info', 'Daily Claim');
      } else {
        showSnackbar(result.error ?? 'Could not claim coins right now.', 'error', 'Daily Claim');
      }
    }
  }, [claimDaily, apiClient, requireAuth, showSnackbar, playSound]);

  const getUserDisplayName = (): string => {
    if (user) {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      if (fullName) return fullName;
      if (user.username) return user.username;
      if (user.primaryEmailAddress) return user.primaryEmailAddress.emailAddress.split('@')[0];
    }
    return 'User';
  };

  const authDisplayName = getUserDisplayName();
  const displayName = optimisticDisplayName ?? authDisplayName;
  const usernameHandle = user?.username ? `@${user.username}` : '';

  const handleStartEditDisplayName = useCallback(() => {
    if (!requireAuth(GUEST_AUTH_ACTIONS.DISPLAY_NAME)) return;
    if (editModeTimeoutRef.current) clearTimeout(editModeTimeoutRef.current);
    setDraftFullName(displayName);
    editModeTimeoutRef.current = setTimeout(() => {
      editModeTimeoutRef.current = null;
      if (isMountedRef.current) setIsEditingDisplayName(true);
    }, EDIT_MODE_DELAY_MS);
  }, [displayName, requireAuth]);

  useEffect(() => { return () => { if (editModeTimeoutRef.current) clearTimeout(editModeTimeoutRef.current); }; }, []);

  useEffect(() => {
    if (optimisticDisplayName != null && authDisplayName === optimisticDisplayName) setOptimisticDisplayName(null);
  }, [optimisticDisplayName, authDisplayName]);

  const handleCancelEditDisplayName = useCallback(() => { setIsEditingDisplayName(false); setDraftFullName(''); }, []);

  const handleDisplayNameChange = useCallback((text: string) => {
    setDraftFullName(sanitizeInput(text, true).slice(0, DISPLAY_NAME_MAX_LENGTH));
  }, []);

  const handleSaveDisplayName = useCallback(async () => {
    if (!requireAuth(GUEST_AUTH_ACTIONS.DISPLAY_NAME)) return;
    const trimmed = sanitizeInput(draftFullName, true).trim();
    if (trimmed.length < DISPLAY_NAME_MIN_LENGTH) { showSnackbar(GLOBAL_MENU_COPY.DISPLAY_NAME_TOO_SHORT, 'warning', GLOBAL_MENU_COPY.FULL_NAME_LABEL); return; }
    if (trimmed.length > DISPLAY_NAME_MAX_LENGTH) { showSnackbar(GLOBAL_MENU_COPY.DISPLAY_NAME_TOO_LONG, 'warning', GLOBAL_MENU_COPY.FULL_NAME_LABEL); return; }
    if (containsProfanity(trimmed)) { showSnackbar(GLOBAL_MENU_COPY.DISPLAY_NAME_NOT_ALLOWED, 'warning', GLOBAL_MENU_COPY.FULL_NAME_LABEL); return; }
    setOptimisticDisplayName(trimmed); setIsEditingDisplayName(false); setDraftFullName('');
    const { firstName, lastName } = splitFullNameToFirstAndLast(trimmed);
    const result = await updateDisplayName(apiClient as ApiClientFn, { firstName, lastName });
    if (result.success) {
      try { await syncDisplayNameToSupabaseSession({ trimmedDisplayName: trimmed, firstName, lastName }); } catch { /* best-effort */ }
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as readonly unknown[];
          if (typeof key[0] === 'string' && DISPLAY_NAME_BEARING_QUERY_ROOTS.includes(key[0])) return true;
          // World Cup leaderboards embed the competitionId at index 1, so match structurally.
          return key[0] === 'worldcup' && (key[2] === 'leaderboard' || key[4] === 'leaderboard');
        },
      });
      showSnackbar('Name updated', 'success');
    } else {
      setOptimisticDisplayName(null);
      const msg = result.message?.trim();
      if (msg === 'No active session' || msg === 'Authentication token not available' || msg === 'AUTH_TIMEOUT') {
        void requireAuth(GUEST_AUTH_ACTIONS.DISPLAY_NAME); return;
      }
      showSnackbar(msg || GLOBAL_MENU_COPY.DISPLAY_NAME_UPDATE_FAILED, 'error', GLOBAL_MENU_COPY.FULL_NAME_LABEL);
    }
  }, [draftFullName, apiClient, requireAuth, showSnackbar, queryClient]);

  const handleOpenTutorial = useCallback(() => {
    track(ANALYTICS_EVENTS.MENU_ITEM_CLICKED, { menu_item: ANALYTICS_MENU_ITEMS.TUTORIAL });
    openTutorialFromMenu();
  }, [track, openTutorialFromMenu]);

  const handleExitImpersonationFromMenu = useCallback(() => {
    setMenuVisible(false);
    setIsClosing(false);
    setOptimisticDisplayName(null);
    slideAnim.setValue(drawerWidthPx);
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.assign(`${window.location.origin}${UNIMPERSONATE_PATH}`);
      return;
    }
    router.replace(UNIMPERSONATE_PATH as Href);
  }, [router, setMenuVisible, slideAnim, drawerWidthPx, setOptimisticDisplayName]);

  // Always-available guest upgrade path from the profile menu (mirrors the impersonation strip).
  const handleUpgradeAccountFromMenu = useCallback(() => {
    track(ANALYTICS_EVENTS.GUEST_SIGNUP_PROMPT_ACCEPTED, { action: GLOBAL_MENU_GUEST_UPGRADE.ACTION });
    setMenuVisible(false);
    setIsClosing(false);
    slideAnim.setValue(drawerWidthPx);
    router.push(buildPublicSignInHref({ signup: true }) as Href);
  }, [track, router, setMenuVisible, slideAnim, drawerWidthPx]);

  // Fire the impression once each time a guest opens the menu (resets on close).
  const guestMenuImpressionRef = useRef(false);
  useEffect(() => {
    if (menuVisible && isAnonymous && !guestMenuImpressionRef.current) {
      guestMenuImpressionRef.current = true;
      track(ANALYTICS_EVENTS.GUEST_SIGNUP_PROMPT_SHOWN, { action: GLOBAL_MENU_GUEST_UPGRADE.ACTION });
    }
    if (!menuVisible) guestMenuImpressionRef.current = false;
  }, [menuVisible, isAnonymous, track]);

  const handleWorldCupRulesPress = useCallback(() => {
    track(ANALYTICS_EVENTS.MENU_ITEM_CLICKED, { menu_item: ANALYTICS_MENU_ITEMS.WORLD_CUP_RULES });
    openWorldCupRulesFromMenu();
    handleDismiss();
  }, [track, openWorldCupRulesFromMenu, handleDismiss]);

  const handlePrivacyToggle = useCallback(() => setIsPrivacySubmenuExpanded((v) => !v), []);

  const handleSelectLegalRow = useCallback((variant: InAppLegalDocumentVariant) => {
    track(ANALYTICS_EVENTS.MENU_ITEM_CLICKED, { menu_item: GLOBAL_MENU_PRIVACY_SUBMENU_LEGAL_ANALYTICS[variant] });
    setPrivacyLegalVariant(variant);
  }, [track]);

  const handleOpenAccountDeletion = useCallback(() => {
    track(ANALYTICS_EVENTS.MENU_ITEM_CLICKED, { menu_item: ANALYTICS_MENU_ITEMS.PRIVACY_ACCOUNT_DELETION_HUB });
    setIsPrivacyAccountDeletionModalVisible(true);
  }, [track]);

  const handleOpenPrivacyDataControls = useCallback(() => {
    track(ANALYTICS_EVENTS.MENU_ITEM_CLICKED, { menu_item: ANALYTICS_MENU_ITEMS.PRIVACY_DATA_CONTROLS });
    setIsPrivacyDataControlsModalVisible(true);
  }, [track]);

  const handleOpenSupport = useCallback(() => {
    handleDismiss(() => setIsSupportModalVisible(true));
  }, [handleDismiss]);

  return (
    <>
    <BaseModal
      isVisible={isModalVisible}
      onDismiss={handleDismiss}
      contentAlignment={getGlobalMenuModalContentAlignment()}
      contentPosition={getGlobalMenuModalContentPosition()}
      contentContainerStyle={styles.drawerWrapper}
      testID={testID || 'global-menu-modal'}
      contentTestID={testID ? `${testID}-content` : GLOBAL_MENU_MODAL_CONTENT_TEST_ID}
      animationType={disableAnimations ? 'none' : 'fade'}
      disableWebBackdropTransition={disableAnimations}
    >
      <Animated.View
        style={[styles.menuContainer, { transform: [{ translateX: slideAnim }] }]}
        pointerEvents="auto"
      >
        <View style={styles.menuInner}>
          <ScrollView
            style={styles.menuScroll}
            contentContainerStyle={styles.menuScrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled
          >
            <MenuProfileSection
              styles={styles}
              theme={theme}
              showWebPressableTransition={showWebPressableTransition}
              disableAnimations={disableAnimations}
              testID={testID}
              currentAvatarId={currentAvatarId}
              currentAvatarConfig={currentAvatarConfig}
              userImageUrl={user?.imageUrl ?? undefined}
              displayName={displayName}
              usernameHandle={usernameHandle}
              onAvatarPress={() => { handleDismiss(() => { router.push('/app/profile'); }); }}
              isEditingDisplayName={isEditingDisplayName}
              draftFullName={draftFullName}
              onStartEdit={handleStartEditDisplayName}
              onCancelEdit={handleCancelEditDisplayName}
              onChangeText={handleDisplayNameChange}
              onSave={handleSaveDisplayName}
              coins={coins}
              onOpenDailyReward={openDailyRewardModal}
              coinIconSource={IDLE_COIN_SOURCE}
              isImpersonatingSession={isImpersonatingSession}
              impersonationTargetLabel={impersonationTargetLabel}
              onExitImpersonation={handleExitImpersonationFromMenu}
              isGuest={isAnonymous}
              onUpgradeAccount={handleUpgradeAccountFromMenu}
            />
            <MenuItemsList
              styles={styles}
              theme={theme}
              showWebPressableTransition={showWebPressableTransition}
              testID={testID}
              isPrivacySubmenuExpanded={isPrivacySubmenuExpanded}
              isMuted={isMuted}
              onToggleSoundMute={toggleMute}
              onOpenDailyReward={openDailyRewardModal}
              onOpenCouponRedeem={openCouponRedeemModal}
              onOpenTutorial={handleOpenTutorial}
              onOpenWorldCupRules={handleWorldCupRulesPress}
              onOpenAvatarCustomize={openLayeredAvatarLabFromMenu}
              onTogglePrivacyMenu={handlePrivacyToggle}
              onSelectLegalRow={handleSelectLegalRow}
              onOpenAccountDeletion={handleOpenAccountDeletion}
              onOpenDataControls={handleOpenPrivacyDataControls}
              onOpenOfficialWebsite={openOfficialWebsite}
              onOpenSupport={handleOpenSupport}
              coinIconSource={IDLE_COIN_SOURCE}
            />

          </ScrollView>

          <View style={styles.signOutFooter}>
            {/* Account switcher (multi-account only) on the left, Sign Out on the right (specs/profile-page). */}
            <OrgSwitcherFooterButton
              testID={testID ? `${testID}-footer-switcher` : 'global-menu-footer-switcher'}
              closeHostThen={closeMenuThenSwitch}
            />
            <SoundPressable
              tapSoundId={SOUND_IDS.UI_TAP_NAV}
              style={({ pressed, hovered }) => [
                styles.menuItemSignOut,
                showWebPressableTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.menuItemSignOutPressed,
                hovered && Platform.OS === 'web' && styles.menuItemSignOutHover,
              ]}
              onPress={() => {
                track(ANALYTICS_EVENTS.MENU_ITEM_CLICKED, { menu_item: ANALYTICS_MENU_ITEMS.SIGN_OUT });
                track(ANALYTICS_EVENTS.SIGN_OUT_CLICKED);
                setMenuVisible(false);
                handleSignOut();
              }}
              testID={testID ? `${testID}-sign-out-button` : 'global-menu-modal-sign-out-button'}
            >
              <LogOut size={22} color={theme.gray?.[600] ?? theme.gray?.[700] ?? '#71717a'} style={styles.menuItemIconLeft} />
              <Text style={styles.menuItemTextSignOut}>Sign Out</Text>
            </SoundPressable>
          </View>
        </View>
      </Animated.View>

      <DailyRewardModal
        isVisible={isDailyRewardModalVisible}
        onDismiss={() => setIsDailyRewardModalVisible(false)}
        onClaim={handleClaimDaily}
        testID={testID ? `${testID}-daily-reward-modal` : 'daily-reward-modal'}
      />
      <CouponRedeemModal
        isVisible={isCouponRedeemModalVisible}
        onDismiss={() => setIsCouponRedeemModalVisible(false)}
        onRedeem={handleRedeemCouponSubmit}
        disableAnimations={disableAnimations}
        testID={testID ? `${testID}-coupon-redeem-modal` : 'coupon-redeem-modal'}
      />
      <PrivacyLegalDocumentModal
        isVisible={privacyLegalVariant !== null}
        variant={privacyLegalVariant}
        onDismiss={() => setPrivacyLegalVariant(null)}
        testID={testID ? `${testID}-privacy-legal-modal` : 'privacy-legal-document-modal'}
      />
      <PrivacyAccountDeletionModal
        isVisible={isPrivacyAccountDeletionModalVisible}
        onDismiss={() => setIsPrivacyAccountDeletionModalVisible(false)}
        testID={testID ? `${testID}-privacy-account-deletion-modal` : 'privacy-account-deletion-modal'}
      />
      <PrivacyDataControlsModal
        isVisible={isPrivacyDataControlsModalVisible}
        onDismiss={() => setIsPrivacyDataControlsModalVisible(false)}
        onOpenAccountDeletion={() => {
          setIsPrivacyDataControlsModalVisible(false);
          setIsPrivacyAccountDeletionModalVisible(true);
        }}
        testID={testID ? `${testID}-privacy-data-controls-modal` : 'privacy-data-controls-modal'}
      />
    </BaseModal>
    <SupportModal
      isVisible={isSupportModalVisible}
      onDismiss={() => setIsSupportModalVisible(false)}
      testID={testID ? `${testID}-support-modal` : 'support-modal'}
    />
    </>
  );
};
